import React from "react";
import * as api from "../../api";
import { ChevronDown, Edit3, Gamepad2, Package, Plus, Trash2 } from "lucide-react";
import { CatalogPortability } from "./CatalogPortability";
import { useAppContext } from "../../AppContext";

/* -----------------------------------------------------------------------------
 * CatalogManagement — admin "JUEGOS" tab. Full CRUD over games + packages
 * (accordion by game). Extracted from AdminPage.jsx.
 * --------------------------------------------------------------------------- */
export function CatalogManagement() {
  const ctx = useAppContext();
  const {
    games, setGames, expandedGames, toggleExpandedGame,
    openCreateGameModal, openEditGameModal, setDeleteGameConfirm,
    handleToggleGameActive, handleUpdateGameCredType,
    setShowAddPkgModal, setEditingPkg, resetPkgForm,
    setPkgGameId, setPkgNameInput, setPkgPriceUsdInput,
    setPkgDescriptionInput, setPkgImageInput,
    setPkgResellerPriceUsdInput, setPkgCostPriceUsdInput, setPkgRequiresVerify,
    setPkgIsBestSellerInput, setPkgVerifyTitle,
    setPkgVerifyDescription, setPkgVerifyImageUrl, setPkgVerifyStepsText,
    handleDeletePackage,
    formatBs, usdToBs, rateReady, showToast,
  } = ctx;

  return (
              <div className="space-y-6" data-testid="admin-catalog-view">
                <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl p-6 flex flex-wrap justify-between items-center gap-3">
                  <div>
                    <h3 className="text-xl font-heading font-extrabold tracking-tight text-white mb-1">Gestión de Juegos, Paquetes y Precios (USD)</h3>
                    <p className="text-xs text-zinc-400">Crea nuevos juegos, agrega paquetes y edita descripciones para los clientes.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <CatalogPortability
                      showToast={showToast}
                      onCatalogChanged={async () => {
                        try {
                          const fresh = await api.listGames();
                          setGames(fresh);
                        } catch (e) { console.warn("games refresh after catalog change failed:", e); }
                      }}
                    />
                    <button onClick={openCreateGameModal}
                      data-testid="admin-open-create-game-modal"
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2 shadow-md">
                      <Gamepad2 className="w-4 h-4" /> Crear Juego
                    </button>
                    <button onClick={() => { setEditingPkg(null); resetPkgForm(); setShowAddPkgModal(true); }}
                      data-testid="admin-open-add-package-modal"
                      className="bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Agregar Paquete
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {games?.map(game => {
                    const isOpen = expandedGames.has(game.id);
                    return (
                    <div key={game.id} className={`rounded-2xl bg-ag7-surface border ${game.is_active === false ? "border-red-900/50" : "border-[var(--ag7-border-soft)]"} overflow-hidden`} data-testid={`admin-game-card-${game.slug}`}>
                      {/* Header — click to toggle. Action buttons stop propagation. */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleExpandedGame(game.id)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpandedGame(game.id); } }}
                        aria-expanded={isOpen}
                        data-testid={`admin-game-header-${game.slug}`}
                        className="flex justify-between items-center gap-3 p-6 cursor-pointer hover:bg-ag7-surface-2/40 transition-colors select-none"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <ChevronDown
                            data-testid={`admin-game-chevron-${game.slug}`}
                            className={`w-4 h-4 text-cyan-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
                          />
                          <img src={game.image} alt={game.name} loading="lazy" decoding="async" className={`w-11 h-11 rounded-xl object-cover border border-[var(--ag7-border-soft)] flex-shrink-0 ${game.is_active === false ? "grayscale opacity-70" : ""}`} />
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-lg text-white truncate">{game.name}</h4>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-[10px] text-zinc-500 font-mono truncate">{game.category}</p>
                              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-ag7-surface-2 text-zinc-400 border border-[var(--ag7-border-soft)] whitespace-nowrap">
                                {(() => {
                                  const ct = game.credentialType || "id";
                                  if (ct === "username_password") return "Usuario + Contraseña";
                                  if (ct === "id_server") return "ID + Server";
                                  return "Solo ID";
                                })()}
                              </span>
                              <span className="text-[9px] font-mono text-zinc-500 whitespace-nowrap">
                                {game.packages.length} paq.
                              </span>
                              {game.is_active === false && (
                                <span data-testid={`admin-game-inactive-badge-${game.slug}`} className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-red-950/60 border border-red-500/40 text-red-300 uppercase tracking-wider">
                                  Pausado
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div data-testid="catalog-management-div-1" className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          {/* Active/Inactive toggle */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleGameActive(game, !(game.is_active !== false)); }}
                            data-testid={`admin-toggle-game-active-${game.slug}`}
                            role="switch"
                            aria-checked={game.is_active !== false}
                            title={game.is_active === false ? "Activar juego (visible en la tienda)" : "Pausar juego (No disponible)"}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors ${
                              game.is_active === false
                                ? "bg-red-950/60 border-red-500/40"
                                : "bg-green-600/30 border-green-500/50"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 rounded-full transition-transform ${
                                game.is_active === false
                                  ? "translate-x-1 bg-red-400"
                                  : "translate-x-6 bg-green-400"
                              }`}
                            />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditGameModal(game); }}
                            data-testid={`admin-edit-game-${game.slug}`}
                            className="p-2 rounded-lg bg-ag7-surface-2 hover:bg-zinc-700 text-cyan-300"
                            title="Editar juego (logo/nombre)"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteGameConfirm(game); }}
                            data-testid={`admin-delete-game-${game.slug}`}
                            className="p-2 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-300"
                            title="Eliminar juego"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Collapsible body */}
                      {isOpen && (
                        <div
                          className="px-6 pb-6 space-y-4 border-t border-[var(--ag7-border-soft)] pt-4"
                          data-testid={`admin-game-body-${game.slug}`}
                        >
                      <div data-testid="catalog-management-div-2"
                        className="flex items-center justify-between text-[10px] font-mono text-zinc-400 uppercase"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>Credencial:</span>
                        <select
                          value={game.credentialType || "id"}
                          onChange={(e) => { e.stopPropagation(); handleUpdateGameCredType(game.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`game-credtype-${game.slug}`}
                          className="bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-2 py-1 text-xs font-mono text-white focus:outline-none focus:border-ag7-purple">
                          <option value="id">Solo ID</option>
                          <option value="username_password">Usuario + Contraseña</option>
                          <option value="id_server">ID + Server</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        {game.packages.length === 0 && (
                          <div className="text-center text-xs text-zinc-500 py-3 font-mono">Aún no hay paquetes. Usa &quot;Agregar Paquete&quot;.</div>
                        )}
                        {game.packages?.map(pkg => (
                          <div key={pkg.id} className="flex justify-between items-start p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-xs font-mono gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              {pkg.image ? (
                                <img src={pkg.image} alt={pkg.name} className="w-10 h-10 rounded-lg object-cover border border-[var(--ag7-border-soft)] flex-shrink-0" data-testid={`admin-pkg-img-${pkg.id}`} />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-ag7-surface-2 border border-[var(--ag7-border-soft)] flex items-center justify-center flex-shrink-0">
                                  <Package className="w-4 h-4 text-zinc-600" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-white truncate">{pkg.name}</p>
                                <span className="text-zinc-500">${pkg.priceUsd.toFixed(2)} USD</span>
                                {pkg.description && (
                                  <p className="text-[10px] text-zinc-400 mt-1 italic line-clamp-2" data-testid={`admin-pkg-desc-${pkg.id}`}>
                                    {pkg.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-cyan-400 font-bold whitespace-nowrap">{formatBs(usdToBs(pkg.priceUsd))}</span>
                              <button data-testid={`edit-pkg-${pkg.id}`} onClick={(e) => {
                                e.stopPropagation();
                                setEditingPkg({ gameId: game.id, pkg });
                                setPkgNameInput(pkg.name);
                                setPkgPriceUsdInput(String(pkg.priceUsd));
                                setPkgResellerPriceUsdInput(pkg.resellerPriceUsd ? String(pkg.resellerPriceUsd) : "");
                                setPkgCostPriceUsdInput(pkg.costPriceUsd ? String(pkg.costPriceUsd) : "");
                                setPkgDescriptionInput(pkg.description || "");
                                setPkgImageInput(pkg.image || "");
                                setPkgGameId(game.id);
                                setPkgRequiresVerify(!!pkg.requiresVerification);
                                setPkgIsBestSellerInput(!!pkg.isBestSeller);
                                const g = pkg.verificationGuide || {};
                                setPkgVerifyTitle(g.title || "");
                                setPkgVerifyDescription(g.description || "");
                                setPkgVerifyImageUrl(g.imageUrl || "");
                                setPkgVerifyStepsText(Array.isArray(g.steps) ? g.steps.join("\n") : "");
                                setShowAddPkgModal(true);
                              }}
                                className="p-1.5 rounded-lg bg-ag7-surface-2 hover:bg-zinc-700 text-purple-300" title="Editar">
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button data-testid={`delete-pkg-${pkg.id}`} onClick={(e) => { e.stopPropagation(); handleDeletePackage(game.id, pkg.id); }}
                                className="p-1.5 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-300" title="Eliminar">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
  );
}
